/**
 * interact_focus — ONE KEY, ONE FOCUS (2026-09-24).
 *
 * Three things inside a building answer E: the door (4.5 m), the structure console (2.4 m) and
 * now the furniture (2.2 m). Each module used to peek at the others' prompt elements to decide
 * whether to yield — a chain that grows with every kind. Instead every module CLAIMS its
 * candidate each frame with its distance and priority; the nearest claim of the previous frame
 * is the focus, and only the focused module shows a prompt and takes the key. Claims live one
 * frame (the frame counter is bumped by whoever reads first each frame), so a module that stops
 * claiming drops out on its own. Pure, no DOM, harness-pinned.
 */

export interface InteractClaim {
  /** The claimant's id ('door', 'console', 'furn:<id>'). */
  id: string;
  /** Distance to the player (m). */
  dist: number;
  /** A tie-breaker: a higher priority wins inside `PRIORITY_BAND_M` of the nearest. */
  priority: number;
  /** What the prompt says. */
  label: string;
}

/** Two claims within this many metres of each other are ranked by priority, not distance. */
export const PRIORITY_BAND_M = 0.6;

export class InteractFocus {
  private claims: InteractClaim[] = [];
  private last: InteractClaim[] = [];
  private frame = 0;
  private stamped = -1;

  /** Advance the frame once per tick (the first caller of a frame flips the buffers). */
  tick(frame: number): void {
    if (frame === this.stamped) return;
    this.stamped = frame;
    this.last = this.claims;
    this.claims = [];
    this.frame = frame;
  }

  claim(c: InteractClaim): void { this.claims.push(c); }

  /** The winning claim of the LAST completed frame (null when nothing was in reach). */
  focus(): InteractClaim | null {
    let best: InteractClaim | null = null;
    for (const c of this.last) {
      if (!best) { best = c; continue; }
      const near = Math.abs(c.dist - best.dist) <= PRIORITY_BAND_M;
      if (near ? c.priority > best.priority || (c.priority === best.priority && c.dist < best.dist) : c.dist < best.dist) best = c;
    }
    return best;
  }

  /** Does this id own the key right now? */
  owns(id: string): boolean { return this.focus()?.id === id; }

  /** The last completed frame's claims (a probe's diagnostic — who asked for the key, and from how far). */
  lastClaims(): readonly InteractClaim[] { return this.last; }

  get currentFrame(): number { return this.frame; }
}

/** The one arbiter (globalThis-backed — the dev graph can resolve a module twice; a shared store is instance-proof). */
export function interactFocus(): InteractFocus {
  const g = globalThis as unknown as { __atlasInteractFocus?: InteractFocus };
  if (!g.__atlasInteractFocus) g.__atlasInteractFocus = new InteractFocus();
  return g.__atlasInteractFocus;
}
